import api from "../api";

class CertificatesService {
  // Get certificates with filtering and pagination
  async getCertificates(filters = {}) {
    try {
      const response = await api.post("/certificates", filters);
      return response.data;
    } catch (error) {
      console.error("Error fetching certificates:", error);
      throw error;
    }
  }

  // Get client dashboard certificates (for CLIENT users only)
  async getClientCertificates(filters = {}) {
    try {
      const response = await api.post("/clients/certificates", filters);
      return response.data;
    } catch (error) {
      console.error("Error fetching client certificates:", error);
      throw error;
    }
  }

  // Get certificate types
  async getCertificateTypes() {
    try {
      const response = await api.get("/certificates/types");
      return response.data;
    } catch (error) {
      console.error("Error fetching certificate types:", error);
      throw error;
    }
  }

  // Get certificate statuses
  async getCertificateStatuses() {
    try {
      const response = await api.get("/certificates/statuses");
      return response.data;
    } catch (error) {
      console.error("Error fetching certificate statuses:", error);
      throw error;
    }
  }

  // Get property types
  async getPropertyTypes() {
    try {
      const response = await api.get("/certificates/property-types");
      return response.data;
    } catch (error) {
      console.error("Error fetching property types:", error);
      throw error;
    }
  }

  // Get clients for certificates
  async getClients() {
    try {
      const response = await api.get("/certificates/clients");
      return response.data;
    } catch (error) {
      console.error("Error fetching clients:", error);
      throw error;
    }
  }

  // Export certificates as CSV
  async exportCSV(filters = {}) {
    try {
      const response = await api.post("/certificates/export/csv", filters, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting certificates:", error);
      throw error;
    }
  }

  // Export certificates as PDF
  async exportPDF(filters = {}) {
    try {
      const response = await api.post("/certificates/export/pdf", filters, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("Error exporting PDF:", error);
      throw error;
    }
  }

  // Get certificate by ID
  async getCertificateById(id) {
    try {
      const response = await api.get(`/certificates/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching certificate:", error);
      throw error;
    }
  }

  // Create new certificate
  async createCertificate(data) {
    try {
      const response = await api.post("/certificates/create", data);
      return response.data;
    } catch (error) {
      console.error("Error creating certificate:", error);
      throw error;
    }
  }

  // Update certificate
  async updateCertificate(id, data) {
    try {
      const response = await api.put(`/certificates/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating certificate:", error);
      throw error;
    }
  }

  // Download certificate file - returns presigned URL (JSON) or PDF blob
  async downloadCertificate(id) {
    try {
      // Always request as blob to preserve binary PDF data
      const response = await api.get(`/certificates/${id}/download`, {
        responseType: 'blob'
      });
      
      // Check Content-Type header to determine response type
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
      
      if (contentType.includes('application/json')) {
        // JSON response (presigned URL) - parse the blob as text then JSON
        const text = await response.data.text();
        try {
          const jsonData = JSON.parse(text);
          return jsonData;
        } catch (parseError) {
          console.error("Error parsing JSON response:", parseError);
          throw new Error("Invalid JSON response from server");
        }
      } else if (contentType.includes('application/pdf')) {
        // PDF blob - return directly without any conversion
        // Ensure it's a proper Blob object
        if (response.data instanceof Blob) {
          return response.data;
        } else {
          // Convert to Blob if it's not already
          return new Blob([response.data], { type: 'application/pdf' });
        }
      } else {
        // Unknown content type - check size to determine type
        const blobSize = response.data instanceof Blob ? response.data.size : 0;
        
        if (blobSize > 0 && blobSize < 1000) {
          // Small response, likely JSON (presigned URL)
          try {
            const text = await response.data.text();
            const jsonData = JSON.parse(text);
            return jsonData;
          } catch (parseError) {
            // Not JSON, return as blob
            return response.data instanceof Blob 
              ? response.data 
              : new Blob([response.data], { type: 'application/pdf' });
          }
        } else {
          // Large response or no size info, assume PDF
          return response.data instanceof Blob 
            ? response.data 
            : new Blob([response.data], { type: 'application/pdf' });
        }
      }
    } catch (error) {
      console.error("Error downloading certificate:", error);
      throw error;
    }
  }

  // Delete certificate
  async deleteCertificate(propertyId, documentId) {
    try {
      const response = await api.delete(
        `/certificates/${propertyId}/certificate/${documentId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting certificate:", error);
      throw error;
    }
  }

  // Archive certificate
  async archiveCertificate(documentId) {
    try {
      const response = await api.post(`/certificates/${documentId}/archive`);
      return response.data;
    } catch (error) {
      console.error("Error archiving certificate:", error);
      throw error;
    }
  }

  // Activate certificate
  async activateCertificate(documentId) {
    try {
      const response = await api.post(`/certificates/${documentId}/active`);
      return response.data;
    } catch (error) {
      console.error("Error activating certificate:", error);
      throw error;
    }
  }

  // Get certificate for editing
  async getCertificateForEdit(propertyId, documentId) {
    try {
      const response = await api.get(
        `/certificates/${propertyId}/certificate/${documentId}/edit`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting certificate for edit:", error);
      throw error;
    }
  }

  // Update certificate
  async updateCertificate(propertyId, documentId, data) {
    try {
      const response = await api.put(
        `/certificates/${propertyId}/certificate/${documentId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error updating certificate:", error);
      throw error;
    }
  }

  // --- Certificate review workflow (reviewer-only) ---

  // Get all certificates pending internal review
  async getReviewQueue() {
    try {
      const response = await api.post("/certificates/review-queue");
      return response.data;
    } catch (error) {
      console.error("Error fetching review queue:", error);
      throw error;
    }
  }

  // Get one pending certificate + the property's current active cert of that type
  async getCertificateForReview(documentId) {
    try {
      const response = await api.get(`/certificates/review/${documentId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching certificate for review:", error);
      throw error;
    }
  }

  // Approve a pending certificate (optionally correcting issue/expiry dates)
  async approveCertificate(documentId, edits = {}) {
    try {
      const response = await api.post(
        `/certificates/review/${documentId}/approve`,
        edits
      );
      return response.data;
    } catch (error) {
      console.error("Error approving certificate:", error);
      throw error;
    }
  }

  // Reject a pending certificate (reason required)
  async rejectCertificate(documentId, rejectionReason) {
    try {
      const response = await api.post(
        `/certificates/review/${documentId}/reject`,
        { rejection_reason: rejectionReason }
      );
      return response.data;
    } catch (error) {
      console.error("Error rejecting certificate:", error);
      throw error;
    }
  }
}

export default new CertificatesService();
